import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Route from 'react-router-dom/Route';
import Switch from 'react-router-dom/Switch';
import matchPath from 'react-router-dom/matchPath';
import { CSSTransition } from 'react-transition-group';
import './FadeSwitch.css';

const NO_MATCH = {
  key: 'no-match'
};

/**
 * Not every location object has a `key` property (e.g. HashHistory).
 */
function getLocationKey(location) {
  return typeof location.key === 'string' ? location.key : '';
}

/**
 * Some superfluous work, but something we need to do in order
 * to persist matches/allow for nesting/etc.
 */
function getMatchedRoute(children, pathname) {
  return (
    React.Children.toArray(children).find(child => {
      return (
        child.props.path &&
        matchPath(pathname, {
          exact: child.props.exact,
          path: child.props.path
        })
      );
    }) || NO_MATCH
  );
}

class FadeSwitch extends Component {
  constructor(props) {
    super(props);

    this.matches = 0;

    this.state = {
      previousLocation: this.props.location,
      isExiting: false,
      isIn: false,
      transitionLoaded: false,
      key: getLocationKey(this.props.location),
      match: getMatchedRoute(this.props.children, this.props.location.pathname)
    };

    this.onEntering = this.onEntering.bind(this);
    this.onEntered = this.onEntered.bind(this);
    this.onExiting = this.onExiting.bind(this);
    this.onExited = this.onExited.bind(this);
  }

  componentDidMount() {
    this.setState({ isIn: true });
  }

  componentWillReceiveProps(nextProps) {
    const nextMatch = getMatchedRoute(
      nextProps.children,
      nextProps.location.pathname
    );
    const { location } = this.props;
    const { location: nextLocation } = nextProps;

    if (this.state.match.key !== nextMatch.key) {
      const { transitionLoaded } = this.state;
      const isExiting = !!transitionLoaded;
      const isIn = !transitionLoaded;

      this.setState({
        match: nextMatch,
        key: getLocationKey(nextProps.location) + ++this.matches,
        previousLocation: Object.assign({ isExiting }, location),
        isExiting: isExiting,
        isIn: isIn
      });
    } else if (location.pathname !== nextLocation.pathname) {
      nextProps.onNestedTransition &&
        nextProps.onNestedTransition(location, nextLocation);
    }
  }

  onEntered(node, isAppearing) {
    const newState = {};
    if (this.state.transitionLoaded) {
      newState.isExiting = false;
    }

    if (!this.state.transitionLoaded) {
      newState.transitionLoaded = true;
    }

    this.setState(newState);
  }

  onEntering() {
    if (this.props.onTransitionInterval) {
      this.props.onTransitionInterval(
        this.state.previousLocation,
        this.props.location
      );
    }
  }

  onExiting() {
    const { location, onTransitionStart } = this.props;
    const { previousLocation } = this.state;

    if (onTransitionStart) {
      onTransitionStart(previousLocation, location);
    }
  }

  onExited(node) {
    this.setState({ isExiting: false, isIn: !this.state.isIn });
  }

  render() {
    const { location, children, transitionProps } = this.props;
    const { previousLocation, isExiting, isIn, transitionLoaded } = this.state;

    return (
      <CSSTransition
        {...transitionProps}
        in={isIn}
        enter={transitionLoaded}
        onEntered={this.onEntered}
        onEntering={this.onEntering}
        onExiting={this.onExiting}
        onExited={this.onExited}
        appear
      >
        <div className="expand-wrap">
          <Switch location={isExiting ? previousLocation : location}>
            {children}
          </Switch>
        </div>
      </CSSTransition>
    );
  }
}

FadeSwitch.propTypes = {
  location: PropTypes.shape({
    key: PropTypes.string,
    pathname: PropTypes.string
  }),
  transitionProps: PropTypes.object,
  onTransitionStart: PropTypes.func,
  onTransitionInterval: PropTypes.func,
  onNestedTransition: PropTypes.func
};

// inject location as a prop so we can listen for changes.
const RouteWrapper = props => (
  <Route
    children={({ location }) => <FadeSwitch location={location} {...props} />}
  />
);

export default RouteWrapper;
